import { ActionTree } from 'vuex'
import { AxiosPromise, AxiosResponse } from 'axios'
import api from '@/services/api'
import scriptsApiService from '@/services/scripts'
import { RootState } from '../../state'
import { ScriptsState } from './state'
import {
  SCRIPTS__CREATE,
  SCRIPTS__UPDATE,
  SCRIPTS__CREATE_OPTION,
  SCRIPTS__DELETE_ITEM,
  SCRIPTS__FETCH,
  SCRIPTS__FETCH_CARDS,
  SCRIPTS__FETCH_ONE,
  SCRIPTS__MAKE_ACTION,
  SCRIPTS__PUBLISH_ITEM,
  SCRIPTS__REMOVE_OPTION,
  SCRIPTS__REMOVE_VARIANT,
  SCRIPTS__STEP_CARD,
  SCRIPTS__UPDATE_OPTION,
} from '../../actions'
import {
  _SCHEME__REMOVE_VARIANT,
  _SCRIPTS__SAVE,
  _SCRIPTS__SAVE_CARD_OPTION,
  _SCRIPTS__SAVE_CARDS,
  _SCRIPTS__SAVE_ONE,
} from '../../mutations'
import {
  CreateOptionPayload,
  CardOption,
  CardInfo,
  ScriptUnit,
  CreateScriptPayload,
  CreateScriptResponse,
  UpdateScriptPayload,
  CreateDraftPayload,
  CreateDraftResponse,
  ScriptActionPayload,
} from '@/models/scripts'
import { UserScope } from '@/models/users'
import {
  CardOptionTypeCreate,
  ScriptAction,
  ScriptPublishStatus,
} from '@/constants/scripts'

export interface GetScriptCardPayload {
  scriptId: string
  cardNumber: number
}

export interface ScriptOptionRemovePayload {
  id: string
  cardId: string
  instanceId: string
  optionId: string
}

export const actions: ActionTree<ScriptsState, RootState> = {
  [SCRIPTS__CREATE](
    context,
    payload: CreateScriptPayload,
  ): AxiosPromise<CreateScriptResponse> {
    return scriptsApiService.create(payload.title)
  },
  [SCRIPTS__UPDATE](context, payload: UpdateScriptPayload) {
    return scriptsApiService.update(payload).then(() => {
      context.dispatch(SCRIPTS__FETCH_ONE, { id: payload.id })
    })
  },
  [SCRIPTS__CREATE_OPTION](
    context,
    payload: CreateOptionPayload,
  ): AxiosPromise<CardOption> {
    const url = `/scripts/${payload.id}/cards/${payload.cardId}/options`
    return api
      .post(url, payload.data)
      .then(async (response: AxiosResponse<CardOption>) => {
        if (
          payload.data.type === CardOptionTypeCreate.Card &&
          payload.data.link === 0
        ) {
          // get data for the all cards
          await context.dispatch(SCRIPTS__FETCH_CARDS, {
            id: payload.id,
            instanceId: payload.instanceId,
          })
        } else {
          context.commit(_SCRIPTS__SAVE_CARD_OPTION, {
            cardId: payload.cardId,
            option: response.data,
          })
        }
        return response
      })
  },
  [SCRIPTS__UPDATE_OPTION](
    context,
    payload: CreateOptionPayload,
  ): AxiosPromise<boolean> {
    const url = `/scripts/${payload.id}/cards/${payload.cardId}/options/${payload.optionId}`
    return api.post(url, payload.data)
  },
  [SCRIPTS__STEP_CARD](
    context,
    payload: GetScriptCardPayload,
  ): AxiosPromise<CardInfo> {
    const url = `/scripts/${payload.scriptId}/cards/n${payload.cardNumber}`
    return api.get(url)
  },
  [SCRIPTS__REMOVE_OPTION](
    context,
    payload: ScriptOptionRemovePayload,
  ): AxiosPromise<boolean> {
    const { id, cardId, instanceId, optionId } = payload
    const url = `/scripts/${id}/cards/${cardId}/options/${optionId}`
    return api.delete(url).then(async (response: AxiosResponse<boolean>) => {
      // TODO: Save showing parameters
      context.dispatch(SCRIPTS__FETCH_ONE, { id })
      await context.dispatch(SCRIPTS__FETCH_CARDS, { id, instanceId })
      // TODO: Restore showing parameters
      return response
    })
  },
  [SCRIPTS__REMOVE_VARIANT](context, payload): AxiosPromise<boolean> {
    const url = `/scripts/${payload.scriptId}/cards/${payload.cardId}/test?variant=${payload.variant}`
    return api.delete(url).then((response: AxiosResponse<boolean>) => {
      context.commit(_SCHEME__REMOVE_VARIANT, payload)
      return response
    })
  },
  [SCRIPTS__FETCH](context): Promise<AxiosResponse<ScriptUnit[]>> {
    return scriptsApiService
      .getScripts({
        withTests: context.getters.canDoScope(UserScope.ScriptsEdit),
        withVersions: context.getters.canDoScope(UserScope.ScriptsPublish),
      })
      .then(response => {
        context.commit(_SCRIPTS__SAVE, response.data)
        return response
      })
  },
  [SCRIPTS__FETCH_CARDS](
    { commit },
    { id, instanceId },
  ): AxiosPromise<CardInfo[]> {
    const url = `/scripts/${id}/cards?expand=content${
      instanceId ? `&instance=${instanceId}` : ''
    }`
    return api.get(url).then((response: AxiosResponse<CardInfo[]>) => {
      commit(_SCRIPTS__SAVE_CARDS, response.data)
      return response
    })
  },
  async [SCRIPTS__FETCH_ONE](
    context,
    payload: ScriptActionPayload,
  ): Promise<AxiosResponse<ScriptUnit>> {
    const response = await scriptsApiService.getScript(payload.id, {
      withTests: context.getters.canDoScope(UserScope.ScriptsEdit),
      withVersions: context.getters.canDoScope(UserScope.ScriptsPublish),
    })

    context.commit(_SCRIPTS__SAVE_ONE, response.data)

    return response
  },
  [ScriptAction.Draft](
    context,
    payload: CreateDraftPayload,
  ): Promise<CreateDraftResponse> {
    const url = `/scripts/${payload.id}/draft`
    return api
      .post(url, { instance: payload.instance })
      .then((response: AxiosResponse<CreateDraftResponse>) => {
        context.dispatch(SCRIPTS__FETCH)
        return response.data
      })
  },
  [SCRIPTS__MAKE_ACTION](
    context,
    payload: ScriptActionPayload,
  ): AxiosPromise<{ publish: ScriptPublishStatus } | boolean> {
    let url = `/scripts/${payload.id}`
    // TODO: need refactoring
    switch (payload.action) {
      case ScriptAction.Clone:
        url += '/clone'
        return api
          .post(url, { instance: payload.instance })
          .then((response: AxiosResponse<boolean>) => {
            context.dispatch(SCRIPTS__FETCH)
            return response
          })
      case ScriptAction.RemoveDraft:
        url += `/draft`
        return api.delete(url).then((response: AxiosResponse<boolean>) => {
          context.dispatch(SCRIPTS__FETCH)
          return response
        })
      case ScriptAction.Publish:
        return context
          .dispatch(SCRIPTS__PUBLISH_ITEM, payload)
          .then((response: AxiosResponse<{ publish: ScriptPublishStatus }>) => {
            context.dispatch(SCRIPTS__FETCH_ONE, { id: payload.id })
            return response
          })
      case ScriptAction.UnPublish:
        url += '/unpublish'
        return api.get(url).then((response: AxiosResponse<boolean>) => {
          context.dispatch(SCRIPTS__FETCH_ONE, { id: payload.id })
          return response
        })
      // TODO: Extract to separate action after API method updated
      case ScriptAction.SaveEdits:
        url += `/pack?publish=false&name=${payload.title}`
        break
      case ScriptAction.PublishEdits:
        url += `/pack?publish=true&name=${payload.title}`
        break
      case ScriptAction.DeleteItem:
        return context.dispatch(SCRIPTS__DELETE_ITEM, payload)
    }
    return api.get(url).then((response: AxiosResponse<boolean>) => {
      context.dispatch(SCRIPTS__FETCH)
      return response
    })
  },
  [SCRIPTS__PUBLISH_ITEM](
    context,
    payload: ScriptActionPayload,
  ): AxiosPromise<ScriptPublishStatus | boolean> {
    const url = `/scripts/${payload.id}/publish`
    return api.post(url, {
      instance: payload.instance,
    })
  },
  [SCRIPTS__DELETE_ITEM](
    context,
    payload: ScriptActionPayload,
  ): AxiosPromise<boolean> {
    const url = `/scripts/${payload.id}`
    return api.delete(url).then((response: AxiosResponse<boolean>) => {
      context.dispatch(SCRIPTS__FETCH)
      return response
    })
  },
}
